import { PermissionGate } from "@components/PermissionGate";
import { useTranslation } from "react-i18next";
import {
  Button,
  Modal,
  NumberInput,
  ScrollAreaAutosize,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import i18n from "src/i18n";
import { z } from "zod";

import { useCreateClinicCollectionMethod } from "@hooks/mutations/clinic/useCreateClinicCollectionMethod";

import { StyledAddCollectionMethodModalContent } from "./AddCollectionMethodModal.styled";


const formSchema = () =>
  z.object({
    method: z.string().min(1, i18n.t("name-required")),
    price: z.number().min(0, i18n.t("price-required")),
  });

type Inputs = z.infer<ReturnType<typeof formSchema>>;

interface AddCollectionMethodModalProps {
  opened: boolean;
  close: () => void;
}

export const AddCollectionMethodModal = ({
  opened,
  close,
}: AddCollectionMethodModalProps) => {
  const { t } = useTranslation();

  const {
    mutate: createCollectionMethod,
    isLoading: isCreateCollectionMethodLoading,
  } = useCreateClinicCollectionMethod();

  const form = useForm({
    mode: "uncontrolled",
    validate: zodResolver(formSchema()),
    initialValues: {
      method: "",
      price: 0,
    },
  });

  const handleFormSubmit = (values: Inputs) => {
    createCollectionMethod(
      {
        method: values.method,
        price: values.price,
      },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  const handleClose = () => {
    form.reset();
    close();
  };

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("add-collection-method")}</Text>}
      opened={opened}
      onClose={handleClose}
      scrollAreaComponent={ScrollAreaAutosize}
      size="md">
      <StyledAddCollectionMethodModalContent>
        <form
          className="form"
          onSubmit={form.onSubmit(
            values => handleFormSubmit(values),
            errors => {
              const firstErrorPath = Object.keys(errors)[0];
              form.getInputNode(firstErrorPath)?.focus();
            }
          )}>
          <div className="form-field">
            <TextInput
              label={t("collection-method")}
              withAsterisk
              {...form.getInputProps("method")}
              key={form.key("method")}
              placeholder={t("eg", { example: "Blood Draw" })}
            />
          </div>

          <div className="form-field">
            <NumberInput
              label={t("price")}
              withAsterisk
              {...form.getInputProps("price")}
              key={form.key("price")}
              min={0}
              step={1}
              placeholder="25"
            />
          </div>

          <div className="form-actions">
            <PermissionGate permission="resources.create"><Button
              loading={isCreateCollectionMethodLoading}
              type="submit"
              ml={"auto"}
              >
              {t("submit")}
            </Button></PermissionGate>
          </div>
        </form>
      </StyledAddCollectionMethodModalContent>
    </Modal>
  );
};
